// client/src/pages/Calendar/CalendarPageInner.js

import React, { useEffect, useState } from "react";
import dayjs from "dayjs";

import CalendarView from "../../components/Calendar/CalendarView";
import CalendarFilters from "../../components/Calendar/CalendarFilters";
import DayDetails from "../../components/Calendar/DayDetails";

import { useCalendar } from "./state/CalendarContext";
import { authGet } from "../../Utilities/Api";

export default function CalendarPageInner() {
    const { state, dispatch } = useCalendar();
    const [filtersOpen, setFiltersOpen] = useState(true);

    useEffect(() => {
        let mounted = true;

        const loadCalendar = async () => {
            dispatch({
                type: "SET_SOURCE_LOADING",
                payload: {
                    source: "calendar",
                    loading: true,
                },
            });

            dispatch({
                type: "CLEAR_SOURCE_ERROR",
                payload: "calendar",
            });

            try {
                const res = await authGet("/calendar");

                if (!res.ok) {
                    throw new Error(
                        `Failed to load calendar: ${res.status}`
                    );
                }

                const data = await res.json();

                if (!mounted) {
                    return;
                }

                dispatch({
                    type: "SET_CALENDAR_DATA",
                    payload: groupCalendarItems(data),
                });
            } catch (err) {
                console.error("Failed to load calendar", err);

                if (!mounted) {
                    return;
                }

                dispatch({
                    type: "SET_SOURCE_ERROR",
                    payload: {
                        source: "calendar",
                        error:
                            err.message ||
                            "The calendar could not be loaded.",
                    },
                });
            } finally {
                if (mounted) {
                    dispatch({
                        type: "SET_SOURCE_LOADING",
                        payload: {
                            source: "calendar",
                            loading: false,
                        },
                    });
                }
            }
        };

        loadCalendar();

        return () => {
            mounted = false;
        };
    }, [dispatch]);

    const handleDateSelect = (date) => {
        const isoDate = dayjs(date).format("YYYY-MM-DD");

        dispatch({
            type: "SET_SELECTED_DATE",
            payload: isoDate,
        });
    };

    const calendarLoading = state.loading.calendar;
    const calendarError = state.errors.calendar;

    if (calendarLoading) {
        return <p style={{ padding: "1rem" }}>Loading calendar...</p>;
    }

    if (calendarError) {
        return (
            <div style={{ padding: "1rem" }}>
                <p>Unable to load the calendar.</p>
                <p>{calendarError}</p>
            </div>
        );
    }

    return (
        <div
            style={{
                display: "flex",
                height: "100vh",
                background: "var(--background)",
                color: "var(--text-primary)",
            }}
        >
            <div
                style={{
                    width: filtersOpen ? "240px" : "60px",
                    borderRight: "1px solid var(--separator-bg)",
                    display: "flex",
                    flexDirection: "column",
                    transition: "width 0.3s ease",
                }}
            >
                <button
                    type="button"
                    onClick={() =>
                        setFiltersOpen((previousValue) => !previousValue)
                    }
                    style={{
                        height: "40px",
                        border: "none",
                        background: "transparent",
                        color: "inherit",
                        cursor: "pointer",
                        borderBottom:
                            "1px solid var(--separator-bg)",
                        fontSize: "0.85rem",
                    }}
                >
                    {filtersOpen ? "Hide Filters" : "Show Filters"}
                </button>

                {filtersOpen && (
                    <div
                        style={{
                            padding: "0.75rem",
                            overflowY: "auto",
                            flex: 1,
                        }}
                    >
                        <CalendarFilters />
                    </div>
                )}
            </div>

            <main
                style={{
                    flex: 1,
                    padding: "1rem",
                    minWidth: 0,
                    overflow: "auto",
                }}
            >
                <CalendarView onSelectDate={handleDateSelect} />
            </main>

            <aside
                style={{
                    width: "300px",
                    borderLeft: "1px solid var(--separator-bg)",
                    padding: "1rem",
                    overflowY: "auto",
                }}
            >
                {state.selectedDate ? (
                    <DayDetails date={state.selectedDate} />
                ) : (
                    <p>Select a date to see details.</p>
                )}
            </aside>
        </div>
    );
}

function groupCalendarItems(items = []) {
    if (!Array.isArray(items)) {
        return {
            tasks: [],
            reminders: [],
            events: [],
            trackers: [],
        };
    }

    return {
        tasks: items.filter((item) => item.type === "task"),

        reminders: items.filter(
            (item) => item.type === "reminder"
        ),

        events: items.filter(
            (item) =>
                item.type === "event" ||
                item.type === "birthday"
        ),

        trackers: items.filter(
            (item) => item.type === "tracker"
        ),
    };
}